import { useEffect, useCallback, useState } from "react";
import { useSocketInstance } from "../context/SocketContext";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router";
import { io } from "socket.io-client";
import { baseURL } from "./utils";
import ChatApi from "../services/chatService";
import NotifiactionApi from "../services/notofiactionService";
import { addMoreChatRoom, selectAllChatRoom, updateCountUnreadMessege, updateMessegeUnreadCount } from "../bmr/slices/chat";

const SocketManager = () => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { pathname } = location;
  const [allRoomJoin, setAllRoomJoin] = useState(false)

  const {
    socketInstance,
    setSocketInstance,
    setTaskManager,
    setMessageRead,
    setNotifiactionCount,
  } = useSocketInstance();

  const { loginId: loginID, role_id } = useSelector((state) => state.loginData);
  const allChatRoom = useSelector(selectAllChatRoom);
  const jwtToken = localStorage.getItem("user");


  //  Initialize socket connection
  useEffect(() => {
    if (jwtToken && !socketInstance) {
      const socket = io(baseURL, {
        reconnection: true,
        reconnectionAttempts: 10,
        reconnectionDelay: 1000,
        extraHeaders: {
          Authorization: jwtToken,
        },
      });

      socket.on("connect", () => {
        if (socket.connected) {
          setSocketInstance(socket);
        }
      });
    }
  }, [jwtToken, socketInstance, setSocketInstance]);

  //  Fetch initial notification and message status
  useEffect(() => {
    const fetchNotificationStatus = async () => {
      try {
        const res = await NotifiactionApi.countNotifiaction();
        setNotifiactionCount(res?.data);
      } catch (e) {
        console.error("Error fetching notifications", e);
      }
    };

    const fetchMessageStatus = async () => {
      try {
        const res = await ChatApi.messageStatus();
        setMessageRead(res?.data?.isNewMessage || false);
      } catch (e) {
        console.error("Error fetching message status", e);
      }
    };

    if (jwtToken) {
      fetchNotificationStatus();
      fetchMessageStatus();
    }
  }, [jwtToken, setNotifiactionCount, setMessageRead]);

  //  Memoized handlers
  const handleReceiveNotification = useCallback((data) => {
    setNotifiactionCount(data);
  }, [setNotifiactionCount]);

  const handleReceiveChatRoom = useCallback(async (data) => {
    // Uncomment to enable join logic
    const exists = allChatRoom.some(item => item.id === data.id);
    if (!exists) {
      await dispatch(addMoreChatRoom(data));
      socketInstance?.emit("join-group", data?.name);
    }
  }, [allChatRoom, dispatch]);

  const handleReceiveMessage = useCallback((data) => {
    const dateKey = Object.keys(data)[0];
    const message = data[dateKey]?.[0];
    if (loginID === message?.receiver_id) {
      setMessageRead(message?.messageCount > 0);
    }
  }, [loginID, setMessageRead]);

  const handleReceiveTasks = useCallback((data) => {
    setTaskManager(data);
  }, [setTaskManager]);


  const handleReceiveUnreadCount = useCallback(async (data) => {
    await dispatch(updateCountUnreadMessege(data))
  }, [allChatRoom])


  const handleReceiveUnreadMessage = useCallback(async (data) => {
    await dispatch(updateMessegeUnreadCount(data))
  }, [])


  // 📡 Set up event listeners
  useEffect(() => {
    if (!socketInstance || !loginID) return;

    if (!allRoomJoin && allChatRoom.length > 0) {
      allChatRoom?.map(op => {
        socketInstance.emit("join-group", op?.name)
      })
      setAllRoomJoin(true)
    }

    // Always needed
    socketInstance.off("receive_notification");
    socketInstance.off("room-created");
    socketInstance.off("unread_count");
    socketInstance.off("unready_message");

    socketInstance.on("receive_notification", handleReceiveNotification);
    socketInstance.on("room-created", handleReceiveChatRoom);
    socketInstance.on("unread_count", handleReceiveUnreadCount);
    socketInstance.on("unready_message", handleReceiveUnreadMessage);

    // Conditional: Only when NOT on /message
    if (pathname !== "/message") {
      if (role_id === 1) {
        socketInstance.emit("count_task");
      } else {
        socketInstance.emit("notify_task_list_count");
      }

      socketInstance.off("receive_message");
      socketInstance.off("receive_task");
      socketInstance.off("receive_task_list");

      socketInstance.on("receive_message", handleReceiveMessage);

      if (role_id === 1) {
        socketInstance.on("receive_task", handleReceiveTasks);
      } else {
        socketInstance.on("receive_task_list", handleReceiveTasks);
      }

      return () => {
        socketInstance.off("receive_message", handleReceiveMessage);
        if (role_id === 1) {
          socketInstance.off("receive_task", handleReceiveTasks);
        } else {
          socketInstance.off("receive_task_list", handleReceiveTasks);
        }
        socketInstance.off("receive_notification", handleReceiveNotification);
        socketInstance.off("room-created", handleReceiveChatRoom);
        socketInstance.off("unread_count", handleReceiveUnreadCount);
        socketInstance.off("unready_message", handleReceiveUnreadMessage);
      };
    }
    // Cleanup for always-needed listeners
    return () => {
      socketInstance.off("receive_notification", handleReceiveNotification);
      socketInstance.off("room-created", handleReceiveChatRoom);
      socketInstance.off("unread_count", handleReceiveUnreadCount);
      socketInstance.off("unready_message", handleReceiveUnreadMessage);
    };
  }, [
    socketInstance,
    loginID,
    pathname,
    role_id,
    handleReceiveNotification,
    handleReceiveChatRoom,
    handleReceiveMessage,
    handleReceiveTasks,
    handleReceiveUnreadCount, // Add this!
  ]);


  return null; // This component only handles side effects
};


export default SocketManager;
